import React from "react"
import Header from "../components/Header"
import Footer from "../components/Footer"
import ProjectNavigation from "../components/ProjectNavigation"
import ResponsiveImage from "../components/ResponsiveImage"

const UnderArmourRookie = () => {
    // Define images array with all presentation slides in order, excluding slide 2
    const presentationSlides = [
        ...Array.from({ length: 1 }, (_, i) => 
            `/images/projects/Under Armour Rookie Project "Second Nature"/SamGastonFinalRookieProjectSummer2025-${String(i + 1).padStart(2, '0')}`
        ),
        ...Array.from({ length: 19 }, (_, i) => 
            `/images/projects/Under Armour Rookie Project "Second Nature"/SamGastonFinalRookieProjectSummer2025-${String(i + 3).padStart(2, '0')}`
        )
    ]
    
    // Function to create an open in new tab button
    const OpenInNewTabButton = ({ imagePath }) => (
        <a 
            href={`${imagePath}.webp`}
            target="_blank" 
            rel="noopener noreferrer"
            className="open-in-new-tab-btn"
        >
            Open in new tab
        </a>
    )

    // Expand/collapse state for desktop text blocks
    const [expandHyper, setExpandHyper] = React.useState(false)
    const [expandIdentity, setExpandIdentity] = React.useState(false)
    const [expandChange, setExpandChange] = React.useState(false)

    // Minimal link-style toggle button
    const toggleLinkStyle = {
        marginTop: "8px",
        background: "none",
        border: "none",
        padding: 0,
        color: "#2563eb",
        cursor: "pointer",
        fontSize: "13px",
        textDecoration: "underline",
        fontFamily: "Lunchtype, sans-serif"
    }

    // Shared text box styling
    const textBoxStyle = {
        // Plain text: no box styling
        padding: 0,
        border: "none",
        borderRadius: 0,
        background: "none",
        boxShadow: "none",
        fontFamily: "Lunchtype, sans-serif"
    }

    const headingStyle = {
        marginTop: "0", 
        marginBottom: "20px", 
        fontFamily: "Fanwood, serif",
        fontSize: "24px",
        fontWeight: "400",
        color: "#111827",
        letterSpacing: "-0.025em"
    }

    const paragraphStyle = {
        marginBottom: "18px",
        fontSize: "15px",
        lineHeight: "1.6",
        color: "#374151"
    }

    const listStyle = {
        marginBottom: "18px",
        paddingLeft: "20px",
        fontSize: "15px",
        lineHeight: "1.6",
        color: "#374151"
    }

    const listItemStyle = {
        marginBottom: "8px"
    }

    // Desktop text box styling with responsive sizing
    const desktopTextBoxStyle = {
        ...textBoxStyle,
        padding: 0,
        fontSize: "14px",
        boxSizing: "border-box"
    }

    const desktopHeadingStyle = {
        ...headingStyle,
        fontSize: "20px",
        marginBottom: "16px"
    }

    const desktopParagraphStyle = {
        ...paragraphStyle,
        fontSize: "14px",
        marginBottom: "14px",
        lineHeight: "1.5"
    }

    const desktopListStyle = {
        ...listStyle,
        fontSize: "14px",
        marginBottom: "14px",
        lineHeight: "1.5"
    }

    const desktopListItemStyle = {
        ...listItemStyle,
        marginBottom: "6px"
    }

    // Hyper Expression content for the 5th slide
    const hyperExpressionContent = (
        <div style={textBoxStyle}>
            <h3 style={headingStyle}>
                Hyper Expression
            </h3>
            <p style={paragraphStyle}>
                My first pillar is Hyper Expression. On this slide, I've used an image from <a href="https://www.instagram.com/watchingnewyork/" target="_blank" rel="noopener noreferrer" style={{ color: "#374151", textDecoration: "underline" }}>@watchingnewyork</a> of a New York City fashion influencer named Treezy.
            </p>
            <p style={paragraphStyle}>
                I've broken down his outfit into five categories that define what Hyper Expression is for Generations Z and Alpha:
            </p>
            <ol style={listStyle}>
                <li style={listItemStyle}>A trend popularized by Playboi Carti and Travis Scott, blending sport and avant-garde fashion.</li>
                <li style={listItemStyle}>A striped sweater reminiscent of Wes Anderson's distinctive style.</li>
                <li style={listItemStyle}>A belt with the Takashi Murakami logomark, central to streetwear.</li>
                <li style={listItemStyle}>A skirt-over-pants look tied to Japanese anti-fashion, referencing Comme des Garçons and Yohji Yamamoto.</li>
                <li style={listItemStyle}>Sandy Liang–inspired ballet and coquettecore bows, especially on footwear.</li>
            </ol>
            <p style={paragraphStyle}>
                These references highlight how outfits from Generations Z and Alpha communicate to multiple sub-audiences rather than one primary audience.
            </p>
            <p style={paragraphStyle}>
                This is a microcosm of an example. Most examples will not fall into these five buckets; it might be different buckets, but the premise remains the same: outfits communicate to multiple sub-audiences rather than one.
            </p>
            <p style={paragraphStyle}>
                Exaggerated silhouettes, bold items, and modular components allow wearers to construct and curate visible identities.
            </p>
            <p style={{ ...paragraphStyle, marginBottom: "0" }}>
                In outdoor, this means styles that are built both for the trail and Instagram.
            </p>
        </div>
    )

    // Full-bleed wrapper (lets slides span the full viewport width)
    const fullBleedStyle = {
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
    }

    const fullBleedInnerStyle = {
        paddingLeft: "20px",
        paddingRight: "20px",
        boxSizing: "border-box",
    }

    return (
        <div>
            <Header />
            <ProjectNavigation currentProject="/under-armour-second-nature" />
            
            <div style={{
                textAlign: "center",
                padding: "30px 0 20px",
                marginBottom: "10px"
            }}>
                <h1 style={{ 
                    fontSize: "36px",
                    margin: "0",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    fontFamily: "Fanwood, serif"
                }}>Under Armour Rookie Project "Second Nature"</h1>
                
                <p style={{
                    maxWidth: "800px",
                    margin: "20px auto 0",
                    lineHeight: "1.7",
                    color: "#333",
                    padding: "0 20px"
                }}>
                    During my Under Armour Outdoor Apparel Design internship, I was tasked with developing a concept apparel line for Generations Z and Alpha to strengthen their connection with Under Armour, with a focus on the outdoor category. The project identified growth opportunities in a segment with high potential. I analyzed generational consumer trends, highlighted the urgency of engaging younger audiences as spending power shifts, and proposed design and brand strategies to position UA as more culturally relevant in the outdoor space.
                </p>
            </div>

            <main>
                <div style={{ 
                    padding: "20px",
                    maxWidth: "min(1500px, calc(100vw - 48px))", 
                    margin: "0 auto"
                }}>
                    <div style={{ 
                        display: "flex",
                        flexDirection: "column",
                        gap: "40px",
                        marginBottom: "50px"
                    }}>
                        {presentationSlides.map((image, index) => {
                            // Check if this is the 5th slide (index 4, which corresponds to slide 6)
                            const isFifthSlide = index === 4;
                            // Check if this is the 6th slide (index 5, which corresponds to slide 7) 
                            const isSixthSlide = index === 5;
                            // Check if this is the 7th slide (index 6, which corresponds to slide 8)
                            const isSeventhSlide = index === 6;
                            
                            if (isFifthSlide) {
                                return (
                                    <div key={`slide-${index}`}>
                                        {/* Desktop layout - centered two-column grid */}
                                        <div style={{ 
                                            display: "grid",
                                            gridTemplateColumns: "minmax(320px, 460px) minmax(0, clamp(560px, 60vw, 900px))",
                                            gap: "32px",
                                            alignItems: "start",
                                            maxWidth: "1500px",
                                            margin: "0 auto",
                                            padding: "0 24px",
                                            boxSizing: "border-box"
                                        }}
                                        className="desktop-text">
                                            {/* Text content on the left for desktop */}
                                            <div style={{
                                                minWidth: 0
                                            }}>
                                                <div style={{
                                                    ...desktopTextBoxStyle,
                                                    maxHeight: expandHyper ? "none" : "clamp(240px, 28vw, 360px)",
                                                    overflow: expandHyper ? "visible" : "hidden"
                                                }}>
                                                    <h3 style={desktopHeadingStyle}>
                                                        Hyper Expression
                                                    </h3>
                                                    <p style={desktopParagraphStyle}>
                                                        My first pillar is Hyper Expression. On this slide, I've used an image from <a href="https://www.instagram.com/watchingnewyork/" target="_blank" rel="noopener noreferrer" style={{ color: "#374151", textDecoration: "underline" }}>@watchingnewyork</a> of a New York City fashion influencer named Treezy.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        I've broken down his outfit into five categories that define what Hyper Expression is for Generations Z and Alpha:
                                                    </p>
                                                    <ol style={desktopListStyle}>
                                                        <li style={desktopListItemStyle}>A trend popularized by Playboi Carti and Travis Scott, blending sport and avant-garde fashion.</li>
                                                        <li style={desktopListItemStyle}>A striped sweater reminiscent of Wes Anderson's distinctive style.</li>
                                                        <li style={desktopListItemStyle}>A belt with the Takashi Murakami logomark, central to streetwear.</li>
                                                        <li style={desktopListItemStyle}>A skirt-over-pants look tied to Japanese anti-fashion, referencing Comme des Garçons and Yohji Yamamoto.</li>
                                                        <li style={desktopListItemStyle}>Sandy Liang–inspired ballet and coquettecore bows, especially on footwear.</li>
                                                    </ol>
                                                    <p style={desktopParagraphStyle}>
                                                        These references highlight how outfits from Generations Z and Alpha communicate to multiple sub-audiences rather than one primary audience.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        This is a microcosm of an example. Most examples will not fall into these five buckets; it might be different buckets, but the premise remains the same: outfits communicate to multiple sub-audiences rather than one.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        Exaggerated silhouettes, bold items, and modular components allow wearers to construct and curate visible identities.
                                                    </p>
                                                    <p style={{ ...desktopParagraphStyle, marginBottom: "0" }}>
                                                        In outdoor, this means styles that are built both for the trail and Instagram.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandHyper(!expandHyper)}
                                                    style={toggleLinkStyle}
                                                    aria-expanded={expandHyper}
                                                >
                                                    {expandHyper ? "Show less" : "Show more"}
                                                </button>
                                            </div>
                                            
                                            {/* Image on the right for desktop */}
                                            <div style={{
                                                minWidth: 0,
                                                display: "flex",
                                                flexDirection: "column"
                                            }}>
                                                <div style={{ 
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    height: "auto",
                                                    margin: "0 auto",
                                                    width: "100%",
                                                    maxWidth: "100%",
                                                    aspectRatio: "16 / 9"
                                                }}>
                                                    <ResponsiveImage 
                                                        src={`${image}.webp`}
                                                        alt={`Under Armour presentation slide ${index + 1}`}
                                                        style={{
                                                            width: "100%",
                                                            height: "auto",
                                                            objectFit: "contain"
                                                        }}
                                                        priority={index === 0}
                                                        loading={index < 3 ? "eager" : "lazy"}
                                                        sizes="(max-width: 767px) 100vw, (max-width: 1200px) 60vw, 900px"
                                                    />
                                                </div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'flex-end',
                                                    maxWidth: "900px",
                                                    margin: "0 auto",
                                                    width: "100%"
                                                }}>
                                                    <OpenInNewTabButton imagePath={image} />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile layout - text below image */}
                                        <div style={{
                                            display: "none",
                                            flexDirection: "column",
                                            gap: "20px"
                                        }}
                                        className="mobile-layout">
                                            <div style={{ 
                                                display: "flex",
                                                justifyContent: "center",
                                                height: "auto",
                                                margin: "0 auto",
                                                maxWidth: "900px",
                                                width: "100%",
                                                aspectRatio: "16 / 9"
                                            }}>
                                                <ResponsiveImage 
                                                    src={`${image}.webp`}
                                                    alt={`Under Armour presentation slide ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain"
                                                    }}
                                                    priority={index === 0}
                                                    loading={index < 3 ? "eager" : "lazy"}
                                                    sizes="100vw"
                                                />
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'flex-end',
                                                maxWidth: "900px",
                                                margin: "0 auto"
                                            }}>
                                                <OpenInNewTabButton imagePath={image} />
                                            </div>
                                            {hyperExpressionContent}
                                        </div>
                                    </div>
                                );
                            }

                            if (isSixthSlide) {
                                return (
                                    <div key={`slide-${index}`}>
                                        {/* Desktop layout - centered two-column grid with text on right */}
                                        <div style={{ 
                                            display: "grid",
                                            gridTemplateColumns: "minmax(0, clamp(560px, 60vw, 900px)) minmax(320px, 460px)",
                                            gap: "32px",
                                            alignItems: "start",
                                            maxWidth: "1500px",
                                            margin: "0 auto",
                                            padding: "0 24px",
                                            boxSizing: "border-box"
                                        }}
                                        className="desktop-text">
                                            {/* Image on the left for desktop */}
                                            <div style={{
                                                minWidth: 0,
                                                display: "flex",
                                                flexDirection: "column"
                                            }}>
                                                <div style={{ 
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    height: "auto",
                                                    margin: "0 auto",
                                                    width: "100%",
                                                    maxWidth: "100%",
                                                    aspectRatio: "16 / 9"
                                                }}>
                                                    <ResponsiveImage 
                                                        src={`${image}.webp`}
                                                        alt={`Under Armour presentation slide ${index + 1}`}
                                                        style={{
                                                            width: "100%",
                                                            height: "auto",
                                                            objectFit: "contain"
                                                        }}
                                                        priority={index === 0}
                                                        loading={index < 3 ? "eager" : "lazy"}
                                                        sizes="(max-width: 767px) 100vw, (max-width: 1200px) 60vw, 900px"
                                                    />
                                                </div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'flex-end',
                                                    maxWidth: "900px",
                                                    margin: "0 auto",
                                                    width: "100%"
                                                }}>
                                                    <OpenInNewTabButton imagePath={image} />
                                                </div>
                                            </div>

                                            {/* Text content on the right for desktop */}
                                            <div style={{
                                                minWidth: 0
                                            }} 
                                            className="desktop-text">
                                                <div style={{
                                                    ...desktopTextBoxStyle,
                                                    maxHeight: expandIdentity ? "none" : "clamp(240px, 28vw, 360px)",
                                                    overflow: expandIdentity ? "visible" : "hidden"
                                                }}>
                                                    <h3 style={desktopHeadingStyle}>
                                                        Identity Pieces
                                                    </h3>
                                                    <p style={desktopParagraphStyle}>
                                                        My second pillar is something I'm calling identity pieces.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        My question is:
                                                    </p>
                                                    <p style={{ ...desktopParagraphStyle, fontStyle: "italic", fontWeight: "500" }}>
                                                        If someone was drawing a caricature of you, what would they draw you wearing?
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        On the bottom of this slide I have 4 outfits of 4 famous cartoon characters. I bet I don't have to tell you who they are. I bet you know just by their clothes.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        Identity pieces are garments that combine utility with standout design elements.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        Inspired by high-performance gear that's often too expensive for frequent purchase, each piece is made to fit seamlessly into an athletic wardrobe while standing out visually.
                                                    </p>
                                                    <p style={{ ...desktopParagraphStyle, marginBottom: "0" }}>
                                                        Blending technical function with expressive form, these high-impact pieces are made to be worn often, recognized instantly, and remembered as signatures of personal style and story. These garments become shorthand for who you are, just like a character's "one outfit," they help people recognize you, wherever you are.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandIdentity(!expandIdentity)}
                                                    style={toggleLinkStyle}
                                                    aria-expanded={expandIdentity}
                                                >
                                                    {expandIdentity ? "Show less" : "Show more"}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile layout - text below image */}
                                        <div style={{
                                            display: "none",
                                            flexDirection: "column",
                                            gap: "20px"
                                        }}
                                        className="mobile-layout">
                                            <div style={{ 
                                                display: "flex",
                                                justifyContent: "center",
                                                height: "auto",
                                                margin: "0 auto",
                                                maxWidth: "900px",
                                                width: "100%",
                                                aspectRatio: "16 / 9"
                                            }}>
                                                <ResponsiveImage 
                                                    src={`${image}.webp`}
                                                    alt={`Under Armour presentation slide ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain"
                                                    }}
                                                    priority={index === 0}
                                                    loading={index < 3 ? "eager" : "lazy"}
                                                    sizes="100vw"
                                                />
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'flex-end',
                                                maxWidth: "900px",
                                                margin: "0 auto"
                                            }}>
                                                <OpenInNewTabButton imagePath={image} />
                                            </div>
                                            <div style={textBoxStyle}>
                                                <h3 style={headingStyle}>
                                                    Identity Pieces
                                                </h3>
                                                <p style={paragraphStyle}>
                                                    My second pillar is something I'm calling identity pieces.
                                                </p>
                                                <p style={paragraphStyle}>
                                                    My question is:
                                                </p>
                                                <p style={{ ...paragraphStyle, fontStyle: "italic", fontWeight: "500" }}>
                                                    If someone was drawing a caricature of you, what would they draw you wearing?
                                                </p>
                                                <p style={paragraphStyle}>
                                                    On the bottom of this slide I have 4 outfits of 4 famous cartoon characters. I bet I don't have to tell you who they are. I bet you know just by their clothes.
                                                </p>
                                                <p style={paragraphStyle}>
                                                    Identity pieces are garments that combine utility with standout design elements.
                                                </p>
                                                <p style={paragraphStyle}>
                                                    Inspired by high-performance gear that's often too expensive for frequent purchase, each piece is made to fit seamlessly into an athletic wardrobe while standing out visually.
                                                </p>
                                                <p style={{ ...paragraphStyle, marginBottom: "0" }}>
                                                    Blending technical function with expressive form, these high-impact pieces are made to be worn often, recognized instantly, and remembered as signatures of personal style and story. These garments become shorthand for who you are, just like a character's "one outfit," they help people recognize you, wherever you are.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            if (isSeventhSlide) {
                                return (
                                    <div key={`slide-${index}`}>
                                        {/* Desktop layout - centered two-column grid with text on left */}
                                        <div style={{ 
                                            display: "grid",
                                            gridTemplateColumns: "minmax(320px, 460px) minmax(0, clamp(560px, 60vw, 900px))",
                                            gap: "32px",
                                            alignItems: "start",
                                            maxWidth: "1500px",
                                            margin: "0 auto",
                                            padding: "0 24px",
                                            boxSizing: "border-box"
                                        }}
                                        className="desktop-text">
                                            {/* Text content on the left for desktop */}
                                            <div style={{
                                                minWidth: 0
                                            }} 
                                            className="desktop-text">
                                                <div style={{
                                                    ...desktopTextBoxStyle,
                                                    maxHeight: expandChange ? "none" : "clamp(240px, 28vw, 360px)",
                                                    overflow: expandChange ? "visible" : "hidden"
                                                }}>
                                                    <h3 style={desktopHeadingStyle}>
                                                        Changing Perceptions
                                                    </h3>
                                                    <p style={desktopParagraphStyle}>
                                                        Changing perceptions is my final pillar.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        Outdoor apparel is undergoing a transformation, driven by a generation that blurs the line between performance and self-expression.
                                                    </p>
                                                    <p style={desktopParagraphStyle}>
                                                        Changing Perceptions captures a shift in how Gen Z and Gen Alpha interact with their environments. Today's outdoor clothing isn't confined to the trail, it's seen at cafes, on campus, in cities, built for movement, for protection, yes, but also for aesthetic. <span style={{ textDecoration: "underline", fontWeight: "500" }}>Gen Z and Gen Alpha have been raised with built-in protection on their clothes as standard. They don't ask if a garment performs, they assume it does.</span>
                                                    </p>
                                                    <p style={{ ...desktopParagraphStyle, marginBottom: "0" }}>
                                                        This concept is not just about changing what we wear outdoors, it's about changing how we define what we call outdoor apparel entirely.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandChange(!expandChange)}
                                                    style={toggleLinkStyle}
                                                    aria-expanded={expandChange}
                                                >
                                                    {expandChange ? "Show less" : "Show more"}
                                                </button>
                                            </div>
                                            
                                            {/* Image on the right for desktop */}
                                            <div style={{
                                                minWidth: 0,
                                                display: "flex",
                                                flexDirection: "column"
                                            }}>
                                                <div style={{ 
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    height: "auto",
                                                    margin: "0 auto",
                                                    width: "100%",
                                                    maxWidth: "100%",
                                                    aspectRatio: "16 / 9"
                                                }}>
                                                    <ResponsiveImage 
                                                        src={`${image}.webp`}
                                                        alt={`Under Armour presentation slide ${index + 1}`}
                                                        style={{
                                                            width: "100%",
                                                            height: "auto",
                                                            objectFit: "contain"
                                                        }}
                                                        priority={index === 0}
                                                        loading={index < 3 ? "eager" : "lazy"}
                                                        sizes="(max-width: 767px) 100vw, (max-width: 1200px) 60vw, 900px"
                                                    />
                                                </div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'flex-end',
                                                    maxWidth: "900px",
                                                    margin: "0 auto",
                                                    width: "100%"
                                                }}>
                                                    <OpenInNewTabButton imagePath={image} />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile layout - text below image */}
                                        <div style={{
                                            display: "none",
                                            flexDirection: "column",
                                            gap: "20px"
                                        }}
                                        className="mobile-layout">
                                            <div style={{ 
                                                display: "flex",
                                                justifyContent: "center",
                                                height: "auto",
                                                margin: "0 auto",
                                                maxWidth: "900px",
                                                width: "100%",
                                                aspectRatio: "16 / 9"
                                            }}>
                                                <ResponsiveImage 
                                                    src={`${image}.webp`}
                                                    alt={`Under Armour presentation slide ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain"
                                                    }}
                                                    priority={index === 0}
                                                    loading={index < 3 ? "eager" : "lazy"}
                                                    sizes="100vw"
                                                />
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'flex-end',
                                                maxWidth: "900px",
                                                margin: "0 auto"
                                            }}>
                                                <OpenInNewTabButton imagePath={image} />
                                            </div>
                                            <div style={textBoxStyle}>
                                                <h3 style={headingStyle}>Changing Perceptions</h3>
                                                <p style={paragraphStyle}>Changing perceptions is my final pillar.</p>
                                                <p style={paragraphStyle}>Outdoor apparel is undergoing a transformation, driven by a generation that blurs the line between performance and self-expression.</p>
                                                <p style={paragraphStyle}>Changing Perceptions captures a shift in how Gen Z and Gen Alpha interact with their environments. Today's outdoor clothing isn't confined to the trail, it's seen at cafes, on campus, in cities, built for movement, for protection, yes, but also for aesthetic. <span style={{ textDecoration: "underline", fontWeight: "500" }}>Gen Z and Gen Alpha have been raised with built-in protection on their clothes as standard. They don't ask if a garment performs, they assume it does.</span></p>
                                                <p style={{ ...paragraphStyle, marginBottom: "0" }}>This concept is not just about changing what we wear outdoors, it's about changing how we define what we call outdoor apparel entirely.</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={`slide-${index}`}>
                                    <div style={fullBleedStyle}>
                                        <div
                                            style={{
                                                ...fullBleedInnerStyle,
                                                display: "flex",
                                                justifyContent: "center",
                                                height: "auto",
                                                margin: "0 auto",
                                                width: "100%",
                                            }}
                                        >
                                            <ResponsiveImage 
                                                src={`${image}.webp`}
                                                alt={`Under Armour presentation slide ${index + 1}`}
                                                style={{
                                                    width: "100%",
                                                    height: "auto",
                                                    objectFit: "contain"
                                                }}
                                                priority={index === 0}
                                                loading={index < 3 ? "eager" : "lazy"}
                                                sizes="100vw"
                                            />
                                        </div>
                                        <div
                                            style={{
                                                ...fullBleedInnerStyle,
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                width: "100%",
                                                margin: "0 auto"
                                            }}
                                        >
                                            <OpenInNewTabButton imagePath={image} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    )
}

export default UnderArmourRookie
